import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ClickAwayListener, Fade, Paper, Popper } from '@mui/material';
import PropTypes from 'prop-types';
import MealSearchHomePage from './meal_search_home_page';
import MealSearchList from './meal_search_list';
import {
  GRC_RESULTS_GROUP_LABEL,
  useMealSearch,
} from '../common/meal_search_hook';
import {
  useLazyGetFullMealQuery,
  useLazyGetGRCRecipesByIDsQuery,
} from '../../app/smorgAppSyncGraphQLApi';
import { userLocale } from '../../services/users';
import {
  useGetOwnedRecipesBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';

const OWN_MEALS_GROUP_LABEL = 'Own meals';

const MealCommunityUserMenuAndSearchComboPopover = ({
  section,
  anchorEl,
  pageRef,
  searchString,
  onAddNewMeal,
  onAddNewNote,
  onAddNewContentEntry,
  onGenerateRecipeAI,
  onGRCMealSelected,
  onOwnMealSelected,
  onDismiss,
  disableSmorgMeals,
  disableBlankRecipe,
  onNavigationKeyDown,
  enableContentEntryCreation,
}) => {
  const { data: userProfile } = useGetUserProfileQuery();

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const [route, setRoute] = useState({ page: 'homePage' });

  const memoizedRecipesBoardIDs = useMemo(
    () => (ownedRecipesBoards || []).map((b) => b.id),
    [ownedRecipesBoards],
  );

  const memoizedRecipesBoardTitles = useMemo(() => {
    const recipesBoardTitles = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const b of ownedRecipesBoards) {
      recipesBoardTitles[b.id] = b.title;
    }
    return recipesBoardTitles;
  }, [ownedRecipesBoards]);

  const allGroupsConfig = useMemo(() => {
    const cfg = [];
    if (memoizedRecipesBoardIDs.length > 0) {
      cfg.push({
        groupLabel: OWN_MEALS_GROUP_LABEL,
        parentIDs: memoizedRecipesBoardIDs,
      });
    }
    if (!disableSmorgMeals) {
      cfg.push({ groupLabel: GRC_RESULTS_GROUP_LABEL });
    }
    return cfg;
  }, [disableSmorgMeals, memoizedRecipesBoardIDs]);

  const locale = userLocale(userProfile);

  const memoizedLocales = useMemo(() => [locale], [locale]);

  const memoizedSearchCriteria = useMemo(
    () => ({
      searchString,
    }),
    [searchString],
  );

  const [results, , , onLoadMore] = useMealSearch(
    memoizedSearchCriteria,
    allGroupsConfig,
    section,
    memoizedLocales,
    true,
  );

  // console.log(JSON.stringify(results));

  const ownMealResults = results.find(
    (groupResults) => groupResults.groupLabel === OWN_MEALS_GROUP_LABEL,
  );
  const smorgMealResults = results.find(
    (groupResults) => groupResults.groupLabel === GRC_RESULTS_GROUP_LABEL,
  );
  // console.log([ownMealResults?.data?.length, smorgMealResults?.data?.length]);
  console.log(results);
  const ownMealsLoading = ownMealResults?.loading;
  const smorgMealsLoading = smorgMealResults?.loading;

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetGRCRecipesByIDs] = useLazyGetGRCRecipesByIDsQuery();

  const getRecipeAndParent = useCallback(
    async (itemID) => {
      /**
       * uniqueMealID is either a meal ID or a GRC recipe ID.
       * Returns a promise which returns an object with the following structure:
       * recipe: a Recipe (not a Meal)
       * parentTitle: title of the recipes board
       * parentLink: router link to the recipes board
       */
      const getRecipeAndParentOperation = (
        uniqueMealID,
        meals,
        grcRecipes,
        boardsTitles,
      ) => {
        console.log(
          `getRecipeAndParentOperation ${uniqueMealID} in ${meals.length} meals and ${grcRecipes.length} GRC recipes`,
        );
        const grcResult = grcRecipes.find(
          (result) => result.grcRecipeID === uniqueMealID,
        );
        if (grcResult) {
          return lazyGetGRCRecipesByIDs([uniqueMealID]).then(
            ({ data: grcRecipesByIDs }) => ({
              recipe: grcRecipesByIDs[uniqueMealID].recipe,
            }),
          );
        }
        const ownMealResult = meals.find(
          (result) => result.mealID === uniqueMealID,
        );
        if (ownMealResult) {
          return lazyGetMeal(uniqueMealID).then(({ data: fullMeal }) => {
            console.log(`Meal parent ${fullMeal.smorgBoardID}`);
            const recipesBoardTitle = boardsTitles[fullMeal.smorgBoardID];
            const parentTitle = recipesBoardTitle || null;
            const parentLink =
              recipesBoardTitle && `/recipes/${fullMeal.smorgBoardID}`;
            console.log({ parentLink, parentTitle });
            return { recipe: fullMeal.recipes[0], parentLink, parentTitle };
          });
        }
        return Promise.resolve({ recipe: null });
      };
      return getRecipeAndParentOperation(
        itemID,
        ownMealResults?.data,
        smorgMealResults?.data,
        memoizedRecipesBoardTitles,
      );
    },
    [
      ownMealResults?.data,
      smorgMealResults?.data,
      memoizedRecipesBoardTitles,
      lazyGetGRCRecipesByIDs,
      lazyGetMeal,
    ],
  );

  const paperRef = useRef();

  const onLoadMoreOwnMeals = () =>
    onLoadMore(OWN_MEALS_GROUP_LABEL, ownMealResults.nextOffset);

  const onLoadMoreSmorgMeals = () =>
    onLoadMore(GRC_RESULTS_GROUP_LABEL, smorgMealResults.nextOffset);

  // console.log(`${(smorgMealResults?.data || []).length} GRC recipe results`);

  return (
    <Popper
      anchorEl={anchorEl}
      open
      placement="bottom-start"
      onClose={onDismiss}
      transition>
      {({ TransitionProps }) => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <Fade {...TransitionProps} timeout={350}>
          {/* tabindex={-1} and outline: 'none' are necessary in order to capture key events */}
          <Paper
            ref={paperRef}
            elevation={3}
            className="light-background"
            style={{
              width: '350px',
              outline: 'none',
              paddingBottom: 10,
            }}
            tabindex={-1}
            onKeyDown={onNavigationKeyDown}>
            <ClickAwayListener
              onClickAway={() => {
                /* do nothing */
              }}>
              <>
                {route.page === 'homePage' && (
                  <MealSearchHomePage
                    pageRef={pageRef}
                    onGRCMealSelected={onGRCMealSelected}
                    onOwnMealSelected={onOwnMealSelected}
                    searchString={searchString}
                    ownMealResults={ownMealResults}
                    ownMealsLoading={ownMealsLoading}
                    smorgMealResults={smorgMealResults}
                    smorgMealsLoading={smorgMealsLoading}
                    onGoToMyMeals={() => setRoute({ page: 'myMeals' })}
                    onGoToSmorgMeals={() => setRoute({ page: 'smorgMeals' })}
                    onAddNewMeal={onAddNewMeal}
                    onAddNewNote={onAddNewNote}
                    onAddNewContentEntry={onAddNewContentEntry}
                    onGenerateRecipeAI={onGenerateRecipeAI}
                    getRecipeAndParent={getRecipeAndParent}
                    onDismiss={onDismiss}
                    disableBlankRecipe={disableBlankRecipe}
                    enableContentEntryCreation={enableContentEntryCreation}
                  />
                )}
                {route.page === 'myMeals' && (
                  <MealSearchList
                    pageRef={pageRef}
                    groupID={route.groupID}
                    // mealResults={ownMealResultsByGroup}
                    mealResults={[
                      {
                        groupID: null,
                        groupLabel: null,
                        results: ownMealResults,
                      },
                    ]}
                    mealsLoading={ownMealsLoading}
                    onMealSelected={onOwnMealSelected}
                    // onLoadMore={(groupID) => onLoadMoreOwnMealsByGroup(groupID)}
                    onLoadMore={() => onLoadMoreOwnMeals()}
                    onBack={() => {
                      setRoute({ page: 'homePage' });
                      // Ensure keyboard events are handled after the click on the Back button
                      paperRef.current.focus();
                    }}
                    onGoToGroup={(groupID) =>
                      setRoute({ page: 'myMeals', groupID })
                    }
                    getRecipeAndParent={getRecipeAndParent}
                    onDismiss={onDismiss}
                  />
                )}
                {route.page === 'smorgMeals' && (
                  <MealSearchList
                    pageRef={pageRef}
                    mealResults={[
                      {
                        groupID: null,
                        groupLabel: null,
                        results: smorgMealResults,
                      },
                    ]}
                    mealsLoading={smorgMealsLoading}
                    onMealSelected={onGRCMealSelected}
                    onLoadMore={() => onLoadMoreSmorgMeals()}
                    onBack={() => {
                      setRoute({ page: 'homePage' });
                      // Ensure keyboard events are handled after the click on the Back button
                      paperRef.current.focus();
                    }}
                    getRecipeAndParent={getRecipeAndParent}
                    onDismiss={onDismiss}
                  />
                )}
              </>
            </ClickAwayListener>
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

MealCommunityUserMenuAndSearchComboPopover.propTypes = {
  section: PropTypes.string.isRequired,
  anchorEl: PropTypes.element.isRequired,
  pageRef: PropTypes.element.isRequired,
  searchString: PropTypes.string.isRequired,
  onAddNewMeal: PropTypes.func.isRequired,
  onAddNewNote: PropTypes.func.isRequired,
  onAddNewContentEntry: PropTypes.func,
  onGenerateRecipeAI: PropTypes.func.isRequired,
  onGRCMealSelected: PropTypes.func.isRequired,
  onOwnMealSelected: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  disableSmorgMeals: PropTypes.bool,
  disableBlankRecipe: PropTypes.bool,
  onNavigationKeyDown: PropTypes.func.isRequired,
  enableContentEntryCreation: PropTypes.bool,
};

MealCommunityUserMenuAndSearchComboPopover.defaultProps = {
  disableSmorgMeals: false,
  disableBlankRecipe: false,
  enableContentEntryCreation: false,
  onAddNewContentEntry: () => {},
};

export default MealCommunityUserMenuAndSearchComboPopover;
